import _, { concat } from "lodash";

import { statusMap } from "../InboxPage";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";

import { format, parseISO } from "date-fns";

import EmailIcon from "@assets/mail.png";
import UploadIcon from "@assets/upload.png";
import MobileSourceIcon from "@assets/mobile.png";
import Attachment from "@assets/attachment.png";

import { DeleteButton, EditButton } from "../../../Components/Buttons.tsx";
import { useBill } from "../../../Components/Contexts/Bill.jsx";

import confirm from "../../../Components/Confirm.tsx";
import {
  CONSUMER_URL
} from "../../../helpers/variables.js";
import { gridTheme } from "../../../Theme/grid-themes.js";

import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { useRef, useState } from "react";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import { useSearchParams } from "react-router-dom";

ModuleRegistry.registerModules([AllCommunityModule]);

const ICON_SIZE = 22;

const Custom = ({ data = [], refresh }) => {
  const [searchParams] = useSearchParams();
  const nimbleAuthID = searchParams.get("nimbleAuthID");

  const { setBill, setLoading } = useBill();

  const invoices = _.chain(data)
    .flatMap((source) =>
      (source.files || []).flatMap((f) =>
        (f.bills || []).map((bill) => ({
          ...bill,
          type: source.type,
        })),
      ),
    )
    .filter((bill) => ![1, 3].includes(bill.bill_status))
    .orderBy(["file_table_id"], ["asc"])
    .value();

  async function notABill(billID) {
    let userConfirmation = await confirm({
      message: "Are you sure you want to delete this bill?",
    });
    if (!userConfirmation) return;

    setLoading(true);
    await fetch(
      `${CONSUMER_URL}/update_bill?bill_id=${billID}&status=3&auth_id=${nimbleAuthID}`,
    );
    setTimeout(() => {
      setLoading(false);
    }, 3000);
    refresh();
  }

  const [selectedRows, setSelectedRows] = useState([]);

  /** @type {import('react').MutableRefObject<import('ag-grid-react').AgGridReact | null>} */
  const gridRef = useRef(null);

  async function downloadBulkFiles() {
    gridRef.current.api.setGridOption("loading", true);

    const checkedRows = gridRef.current.api.getSelectedRows();
    const uniqueRows = Array.from(
      new Map(checkedRows.map((row) => [row.s3_key, row])).values(),
    );

    if (uniqueRows.length === 1) {
      const row = uniqueRows[0];
      const response = await fetch(row.preassigned_url);
      const blob = await response.blob();

      saveAs(blob, row.source_filename);
    } else {
      const zip = new JSZip();
      const files = await Promise.all(
        uniqueRows.map(async (row, index) => {
          const response = await fetch(row.preassigned_url);
          const blob = await response.blob();

          zip.file(row.source_filename, blob);
        }),
      );
      const content = await zip.generateAsync({ type: "blob" });

      saveAs(content, "files.zip");
    }

    gridRef.current.api.deselectAll();
    gridRef.current.api.setGridOption("loading", false);
  }

  return (
    <Stack flexGrow={1}>
      {selectedRows.length > 0 && (
        <Button sx={{ alignSelf: "flex-start" }} onClick={downloadBulkFiles}>
          Download
        </Button>
      )}
      <AgGridReact
        ref={gridRef}
        enableBrowserTooltips
        getRowId={(params) => params.data._id}
        rowData={invoices}
        rowSelection={{
          mode: "multiRow",
          checkboxes: false,
        }}
        selectionColumnDef={{
          width: 80,
          cellStyle: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          },
          cellRenderer: (params) => {
            return (
              <>
                <img
                  src={Attachment}
                  style={{
                    height: 16,
                    width: 16,
                    marginRight: 12,
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    window.parent.postMessage(
                      JSON.stringify({
                        source: "nimbleio",
                        screen: "attachment",
                        bill_id: params.data._id,
                      }),
                      "*",
                    )
                  }
                />
                <input
                  type="checkbox"
                  checked={params.node.isSelected()}
                  onChange={(e) => params.node.setSelected(e.target.checked)}
                />
              </>
            );
          },
        }}
        suppressCellFocus={true}
        onSelectionChanged={(e) => setSelectedRows(e.api.getSelectedRows())}
        noRowsOverlayComponent={() => <>No Data Available</>}
        columnDefs={[
          {
            headerName: "Number",
            field: "invoice_number",
            tooltipField: "invoice_number",
            // filter:true,
            flex: 1,
          },
          {
            headerName: "Bill Date",
            field: "invoice_date",
            tooltipField: "invoice_date",
            flex: 1,
          },
          {
            headerName: "Payee Name",
            field: "vendor_name",
            tooltipField: "vendor_name",
            flex: 1,
          },
          {
            headerName: "Account #",
            field: "account_number",
            tooltipField: "account_number",
            flex: 1,
          },
          {
            headerName: "Due Date",
            field: "due_date",
            tooltipField: "due_date",
            flex: 1,
          },
          {
            headerName: "Amount",
            field: "invoice_amount",
            flex: 1,
            cellStyle: { textAlign: "right" },
          },
          {
            headerName: "File Name",
            field: "source_filename",
            tooltipField: "source_filename",
            flex: 1,
          },
          {
            headerName: "Received via",
            field: "type",
            flex: 1,
            cellStyle: { textAlign: "center" },
            cellRenderer: ({ value }) => {
              const isUpload = ["upload", "mobile"].includes(value);
              const iconSrc =
                value === "mobile"
                  ? MobileSourceIcon
                  : isUpload
                    ? UploadIcon
                    : EmailIcon;

              return (
                <img
                  src={iconSrc}
                  style={{ height: ICON_SIZE, width: ICON_SIZE }}
                />
              );
            },
          },
          {
            headerName: "Received Date",
            field: "receivedDateTime",
            flex: 1,
            valueFormatter: ({ value }) =>
              value ? format(parseISO(value), "MM/dd/yyyy") : "",
            tooltipValueGetter: ({ value }) =>
              value ? format(parseISO(value), "MM/dd/yyyy") : "",
          },
          {
            headerName: "Status",
            field: "bill_status",
            flex: 1,
            cellStyle: { textAlign: "center" },
            cellRenderer: ({ value }) => {
              let status = statusMap[value];
              let label = status?.name || "In Process";
              let color = status?.color || "primary";

              return (
                <Box title={label}>
                  <Chip color={color} label={label} />
                </Box>
              );
            },
          },
          {
            headerName: "Reason",
            field: "error_description",
            tooltipField: "error_description",
            flex: 2,
            cellRenderer: ({ value }) => {
              if (!value) return "";

              const lines = value.split(/\r?\n/);

              if (lines.length > 1) {
                return (
                  <Stack direction={"row"} alignItems={"center"} gap={0.2}>
                    <Box overflow={"hidden"} textOverflow={"ellipsis"}>
                      {lines[0]}
                    </Box>
                    <Box>+{lines.length - 1}</Box>
                  </Stack>
                );
              }

              return value;
            },
          },
          {
            headerName: "Action",
            field: "_id",
            width: 100,
            cellStyle: { textAlign: "center" },
            cellRenderer: ({ data, value }) => {
              return (
                <>
                  <EditButton mr={2} onClick={() => setBill(data)} />
                  <DeleteButton onClick={() => notABill(value)} />
                </>
              );
            },
          },
        ]}
        defaultColDef={{
          resizable: false,
          sortable: false,
          suppressMovable: true,
        }}
        enableCellTextSelection
        theme={gridTheme}
      />
    </Stack>
  );
};

export default Custom;
